import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppToast } from "@/hooks/useAppToast";

interface InfiniteScrollProps<T> {
  fetchItems: (page: number) => Promise<T[]>;
  renderItem: (item: T, key: React.Key) => React.ReactNode;
  itemKey: (item: T) => React.Key;
  refreshHandle?: { token: number; refresh: () => void };
  layout?: "grid" | "list";
  gridCols?: {
    default: number;
    lg?: number;
  };
  showRefreshButton?: boolean;
}

export function InfiniteScroll<T>({
  refreshHandle,
  fetchItems,
  renderItem,
  itemKey,
  layout = "list",
  gridCols = { default: 1 },
  showRefreshButton = true,
}: InfiniteScrollProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const mountedRef = useRef(false);
  const initialLoadRef = useRef(false);

  const toast = useAppToast();

  const loadItems = useCallback(
    async (isInitialLoad: boolean = false) => {
      if (loading || (!hasMore && !isInitialLoad)) return;
      setLoading(true);
      try {
        const newItems = await fetchItems(isInitialLoad ? 1 : page);
        if (newItems.length === 0) {
          setHasMore(false);
        } else {
          setItems(
            isInitialLoad
              ? newItems
              : (prevItems) => [...prevItems, ...newItems]
          );
          setPage(isInitialLoad ? 2 : (prevPage) => prevPage + 1);
        }
      } catch {
        toast.error("Failed to load items. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [fetchItems, page, loading, hasMore, toast]
  );

  const refreshItems = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadItems(true);
    } catch {
      toast.error("Failed to refresh items. Please try again.");
    } finally {
      setRefreshing(false);
    }
  }, [loadItems]);

  // Reset state on mount (when key changes)
  useEffect(() => {
    // Reset all state when component mounts
    setItems([]);
    setPage(1);
    setHasMore(true);
    setLoading(false);
    mountedRef.current = false;
    initialLoadRef.current = false;
  }, []);

  // Initial load
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (!initialLoadRef.current) {
        initialLoadRef.current = true;
        loadItems(true);
      }
    }
  }, [loadItems]);

  // Intersection observer setup
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !loading &&
          hasMore &&
          items.length > 0
        ) {
          loadItems(false);
        }
      },
      { threshold: 1.0 }
    );

    if (loaderRef.current) {
      observerRef.current.observe(loaderRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadItems, loading, hasMore, items.length]);

  useEffect(() => {
    if (refreshHandle?.token) {
      refreshItems();
    }
  }, [refreshHandle?.token]);

  const getGridClassName = () => {
    if (layout !== "grid") return "space-y-6";

    const baseClasses = "grid gap-6";
    const colClasses = {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
    };
    const lgColClasses = {
      1: "lg:grid-cols-1",
      2: "lg:grid-cols-2",
      3: "lg:grid-cols-3",
      4: "lg:grid-cols-4",
    };

    const defaultCols =
      colClasses[gridCols.default as keyof typeof colClasses] || colClasses[1];
    const lgCols = gridCols.lg
      ? lgColClasses[gridCols.lg as keyof typeof lgColClasses]
      : "";

    return `${baseClasses} ${defaultCols} ${lgCols}`.trim();
  };

  return (
    <div className={getGridClassName()}>
      {items.map((item) => renderItem(item, itemKey(item)))}
      <div className={layout === "grid" ? "col-span-full" : ""}>
        {loading && <div>Loading...</div>}
        {!loading && hasMore && <div ref={loaderRef}>Load more</div>}
        {!hasMore && showRefreshButton && (
          <div>
            <button onClick={refreshItems} disabled={refreshing}>
              {refreshing ? "Refreshing..." : "Check for new items"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default InfiniteScroll;
